import { computed, getCurrentInstance, onBeforeUnmount, ref } from "vue";
import { getEmployeeReadinessRecalcStatus, updateEmployeeReadiness } from "@/api";

export const READINESS_RECALC_POLL_INTERVAL_MS = 2000;
export const READINESS_RECALC_POLL_CAP_MS = 120000;
export const READINESS_RECALC_FAILED_MESSAGE =
  "Readiness couldn't be updated. Refresh the page to try again.";

const isTerminal = (state) => state === "done" || state === "failed";

/**
 * One status poll per employee after an importance change. The caller supplies
 * `refetch(ids)`, which re-fetches once and patches values in place when the
 * recalculation is done; no per-tick table reload happens here.
 */
export function useReadinessRecalcRefresh() {
  const pendingIds = ref([]);
  const failedIds = ref([]);
  const isPending = computed(() => pendingIds.value.length > 0);
  const hasFailed = computed(() => failedIds.value.length > 0);

  let session = null;

  const clearTimer = () => {
    if (session?.timer) clearTimeout(session.timer);
    if (session) session.timer = null;
  };

  const cancel = () => {
    if (session) {
      clearTimer();
      session.cancelled = true;
      session = null;
    }
    pendingIds.value = [];
  };

  const finish = (current) => {
    if (session !== current) return;
    session = null;
    pendingIds.value = [];
  };

  const fail = (current) => {
    if (session !== current || current.cancelled) return;
    const ids = [...current.ids];
    finish(current);
    failedIds.value = ids;
    current.onFailed?.(READINESS_RECALC_FAILED_MESSAGE);
  };

  const complete = async (current) => {
    const version = current.version;
    try {
      await current.refetch([...current.ids]);
    } catch (error) {
      console.error("Error refreshing readiness after recalculation:", error);
    }
    if (session !== current || current.cancelled) return;
    if (current.version !== version) {
      current.deadline = Date.now() + READINESS_RECALC_POLL_CAP_MS;
      schedule(current);
      return;
    }
    finish(current);
  };

  const tick = async (current) => {
    current.timer = null;
    if (session !== current || current.cancelled) return;
    if (Date.now() >= current.deadline) {
      fail(current);
      return;
    }
    let state = null;
    try {
      const { data } = await getEmployeeReadinessRecalcStatus(
        current.accountId,
        current.employeeId
      );
      state = data?.recalc_state;
    } catch (error) {
      if (error?.response?.status === 404) {
        if (session === current && !current.cancelled) fail(current);
        return;
      }
    }
    if (session !== current || current.cancelled) return;
    if (state === "failed") {
      fail(current);
    } else if (isTerminal(state)) {
      await complete(current);
    } else {
      schedule(current);
    }
  };

  function schedule(current) {
    clearTimer();
    current.timer = setTimeout(() => tick(current), READINESS_RECALC_POLL_INTERVAL_MS);
  }

  /**
   * Add `ids` (row ids whose readiness is pending) to the scope of the single
   * poll for this employee, starting it if none is running.
   */
  const start = async ({ accountId, employeeId, ids = [], refetch, onFailed }) => {
    if (session && String(session.employeeId) !== String(employeeId)) cancel();
    failedIds.value = [];

    let current = session;
    if (current) {
      current.version += 1;
      current.refetch = refetch;
      current.onFailed = onFailed;
      current.deadline = Date.now() + READINESS_RECALC_POLL_CAP_MS;
      ids.forEach((id) => current.ids.add(id));
    } else {
      current = {
        accountId,
        employeeId,
        ids: new Set(ids),
        refetch,
        onFailed,
        version: 0,
        deadline: Date.now() + READINESS_RECALC_POLL_CAP_MS,
        timer: null,
        cancelled: false,
      };
      session = current;
      schedule(current);
    }
    pendingIds.value = [...current.ids];

    try {
      await updateEmployeeReadiness(accountId, employeeId);
    } catch (error) {
      console.error("Error triggering readiness recalculation:", error);
      fail(current);
    }
  };

  if (getCurrentInstance()) onBeforeUnmount(cancel);

  return { pendingIds, failedIds, isPending, hasFailed, start, cancel };
}
